import type {
  ExtractPurchaseOrder200, SearchPoVariants200Item, SearchPoCustomers200Item,
  CreatePoDraftOrderBody, CreatePoDraftOrder200,
  ListPoMappings200Item, SavePoMappingBody, UpdatePoMappingBody, SavePoMapping200, UpdatePoMapping200, DeletePoMapping200,
} from '@workspace/api-client-react';

export type PoReview = ExtractPurchaseOrder200;
export type PoVariant = SearchPoVariants200Item;
export type PoCustomer = SearchPoCustomers200Item;
export type PoCreateInput = CreatePoDraftOrderBody;
export type PoCreatedDraft = CreatePoDraftOrder200;
export type PoAddress = PoCreateInput['shippingAddress'];

export type PoMapping = ListPoMappings200Item;
export type PoMappingInput = SavePoMappingBody;
export type PoMappingUpdate = UpdatePoMappingBody;

const BASE = import.meta.env.BASE_URL.replace(/\/$/, '');
async function request<T>(path: string, token: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${BASE}/api/po-draft-import${path}`, {
    ...init, headers: { Authorization: `Bearer ${token}`, ...init.headers },
  });
  const body = await response.json().catch(() => null);
  if (!response.ok) throw new Error(body?.message ?? body?.error ?? `Request failed (${response.status}). Please try again.`);
  if (!body) throw new Error('The server returned an unreadable response. No success has been assumed.');
  return body as T;
}

export const poDraftImportApi = {
  extract: (storeId: string, file: File, token: string, signal?: AbortSignal) =>
    request<PoReview>(`/extract?${new URLSearchParams({ storeId, filename: file.name })}`, token, {
      method: 'POST', headers: { 'Content-Type': 'application/pdf' }, body: file, signal,
    }),
  variants: (storeId: string, q: string, token: string, signal?: AbortSignal) =>
    request<PoVariant[]>(`/variants?${new URLSearchParams({ storeId, q })}`, token, { signal }),
  customers: (storeId: string, q: string, token: string, signal?: AbortSignal) =>
    request<PoCustomer[]>(`/customers?${new URLSearchParams({ storeId, q })}`, token, { signal }),
  create: (input: PoCreateInput, token: string) =>
    request<PoCreatedDraft>('/create', token, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input),
    }),
  listMappings: (storeId: string, token: string, signal?: AbortSignal) =>
    request<PoMapping[]>(`/mappings?${new URLSearchParams({ storeId })}`, token, { signal }),
  saveMapping: (input: PoMappingInput, token: string) =>
    request<SavePoMapping200>('/mappings', token, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }),
  updateMapping: (id: string, input: PoMappingUpdate, token: string) =>
    request<UpdatePoMapping200>(`/mappings/${encodeURIComponent(id)}`, token, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) }),
  deleteMapping: (id: string, storeId: string, token: string) =>
    request<DeletePoMapping200>(`/mappings/${encodeURIComponent(id)}?${new URLSearchParams({ storeId })}`, token, { method: 'DELETE' }),
};
